import { RelationshipLabel, UID } from '@askporter/grieg-types';
import { BatchSummary, MutationResultItem, CreateRelationshipMutation } from '../types';

interface BatchPromiseItem {
  promise: Promise<MutationResultItem>;
  aParty: string;
  bParty: string;
  relLabel: RelationshipLabel;
}

/**
 * Generic function for linking an array of UIDs to an entity with a provided relationship label
 *
 * @param party - the party type (aParty or bParty)
 * @param partyUID - the party UID, all items provided later will be linked to this party
 * @param relLabel - the relationship label
 * @param addRelationship - the react-query mutation function to call
 *
 * @returns an async function - that accepts an itemsToLink (array of UIDs) and returns a BatchSummary as a result
 */
export const linkItems =
  (
    party: 'aParty' | 'bParty',
    partyUID: UID,
    relLabel: RelationshipLabel,
    addRelationship: CreateRelationshipMutation,
  ) =>
  async (itemsToLink: Array<UID>): Promise<BatchSummary> => {
    const batchSize = itemsToLink?.length || 0;
    const batchPromises: Array<BatchPromiseItem> = [];

    itemsToLink?.forEach((uid) => {
      const aParty = party === 'aParty' ? partyUID : uid;
      const bParty = party === 'bParty' ? partyUID : uid;

      batchPromises.push({
        promise: addRelationship.mutateAsync({
          aParty,
          relationshipLabel: relLabel,
          bParty,
        }),
        aParty,
        bParty,
        relLabel,
      });
    });

    // as promise all would terminate on an error, iterate through and return the relevant information
    const results: Array<MutationResultItem> = await Promise.all(
      batchPromises.map((batchPromiseItem: BatchPromiseItem) => {
        const { aParty, bParty, relLabel } = batchPromiseItem;
        return batchPromiseItem.promise
          .then((result) => result)
          .catch((error: unknown) => {
            return { error, aParty, bParty, relLabel };
          });
      }),
    );

    return summariseBatch(results, batchSize);
  };

/**
 * Uses resolved promises to generate a batch summary
 *
 * @returns a summary of the batch detailing success status & errors
 */
const summariseBatch = (results: Array<MutationResultItem>, batchSize: number): BatchSummary => {
  const batchSummary: BatchSummary = { results, batchSize, noOfErrors: 0, status: 'SUCCESS' };

  // check for errors
  batchSummary.noOfErrors = batchSummary.results.reduce((acc, curr) => {
    if ('error' in curr) return acc + 1;
    else return acc;
  }, 0);

  // assign status
  if (batchSummary.noOfErrors > 0 && batchSummary.noOfErrors < batchSummary.batchSize)
    batchSummary.status = 'PARTIAL_SUCCESS';
  if (batchSummary.noOfErrors === batchSummary.batchSize) batchSummary.status = 'FAILURE';

  return batchSummary;
};
